import axios from 'axios';
import { Question } from './questionService';
import { CONFIG } from '../config/constants';

// Define API base URL from constants
const API_URL = CONFIG.API.API_URL;

// Define types for Quiz Management
export interface Quiz {
  id: number;
  user_id: number;
  title: string;
  description: string | null;
  time_limit: number | null; // in minutes, null = no limit
  shuffle_questions: boolean;
  shuffle_answers: boolean;
  is_public: boolean;
  require_login: boolean;
  max_attempts: number | null; // null = unlimited
  show_results: boolean;
  show_correct_answers: boolean;
  questions_count: number;
  attempts_count: number;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    name: string;
    email: string;
  };
  quiz_questions?: QuizQuestion[];
}

export interface QuizQuestion {
  id: number;
  quiz_id: number;
  question_id: number;
  order_index: number;
  points: number;
  question: Question;
}

export interface CreateQuizData {
  title: string;
  description?: string;
  question_ids: number[];
  time_limit?: number | null;
  shuffle_questions?: boolean;
  shuffle_answers?: boolean;
  is_public?: boolean;
  require_login?: boolean;
  max_attempts?: number | null;
  show_results?: boolean;
  show_correct_answers?: boolean;
  points_per_question?: { [key: string]: number };
}

export interface UpdateQuizData {
  title?: string;
  description?: string;
  time_limit?: number | null;
  shuffle_questions?: boolean;
  shuffle_answers?: boolean;
  is_public?: boolean;
  require_login?: boolean;
  max_attempts?: number | null;
  show_results?: boolean;
  show_correct_answers?: boolean;
}

export interface QuizListResponse {
  success: boolean;
  data: {
    data: Quiz[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface AvailableQuestionsResponse {
  questions: Question[];
  total: number;
}

// Define types for Quiz Taking
export interface QuizTakeInfo {
  id: number;
  title: string;
  description: string | null;
  time_limit: number | null;
  shuffle_questions: boolean;
  shuffle_answers: boolean;
  is_public: boolean;
  require_login: boolean;
  max_attempts: number | null;
  show_results: boolean;
  show_correct_answers: boolean;
  questions_count: number;
  user_attempts_count?: number;
  can_take_quiz: boolean;
  attempt_id?: number;
  shuffle_mapping?: {
    questions?: {
      original_order: number[];
      shuffled_order: number[];
    };
    answers?: {
      [questionId: number]: {
        original_order: number[];
        shuffled_order: number[];
        mapping: { [shuffledPos: number]: number };
      };
    };
  };
  questions: QuizTakeQuestion[];
}

export interface QuizTakeQuestion {
  id: number;
  text: string;
  answer_objects: Array<{ id: string; text: string; }>;
  points: number;
  order_index: number;
  original_order?: number[] | null;
}

export interface StartQuizData {
  user_info?: {
    name?: string;
    email?: string;
  };
}

export interface QuizAttempt {
  id: number;
  quiz_id: number;
  user_id: number | null;
  user_info: {
    name?: string;
    email?: string;
  } | null;
  started_at: string;
  submitted_at: string | null;
  score: string | number | null; // Laravel decimal cast returns string
  total_score: string | number | null; // Laravel decimal cast returns string
  percentage: string | number | null; // Laravel decimal cast returns string
  time_taken: number | null; // in seconds
  ip_address: string;
  actual_question_count: number; // Số câu hỏi tại thời điểm làm quiz
  quiz: Quiz;
}

export interface SubmitQuizData {
  attempt_id: number;
  answers: {
    question_id: number;
    selected_answer_id: string;
  }[];
}

export interface SubmitQuizResponse {
  success: boolean;
  message: string;
  data: {
    attempt_id: number;
    score: number;
    total_score: number;
    percentage: number;
    correct_answers: number;
    total_questions: number;
    time_taken: number;
    results?: any[];
  };
}

export interface QuizResult {
  quiz: Quiz;
  attempt: QuizAttempt;
  questions: QuizQuestionResult[];
}

export interface QuizQuestionResult {
  id: number;
  question_id: number;
  quiz_question_id: number;
  selected_answer_index: number | null;
  selected_answer_id: string | null;
  correct_answer_index: number;
  correct_answer_id: string | null;
  is_correct: boolean;
  answered: boolean;
  points_earned: number;
  time_spent: number | null; // in seconds
  question: {
    id: number;
    text: string;
    options: string[];
    answer_options: Array<{
      id: string;
      text: string;
    }>;
    type: string;
    explanation: string | null;
  };
}

export interface UserQuizAttempts {
  attempts: QuizAttempt[];
  total: number;
  current_page: number;
  last_page: number;
  per_page: number;
}

// Configure axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Accept': 'application/json',
  },
});

// Add a request interceptor to include the token in requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Quiz service
const quizService = {
  // ============================================
  // QUIZ MANAGEMENT (Authenticated users only)
  // ============================================

  // Get all quizzes for authenticated user (paginated)
  getQuizzes: async (page: number = 1, filters?: {
    search?: string;
    is_public?: boolean;
    per_page?: number;
  }): Promise<QuizListResponse> => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        ...(filters?.search && { search: filters.search }),
        ...(filters?.is_public !== undefined && { is_public: filters.is_public.toString() }),
        ...(filters?.per_page && { per_page: filters.per_page.toString() }),
      });

      const response = await apiClient.get(`/quizzes?${params}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Failed to fetch quizzes');
    }
  },

  // Get a specific quiz (with questions)
  getQuiz: async (quizId: number): Promise<Quiz> => {
    try {
      const response = await apiClient.get(`/quizzes/${quizId}`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Failed to fetch quiz');
    }
  },

  // Create a new quiz
  createQuiz: async (data: CreateQuizData): Promise<Quiz> => {
    try {
      const response = await apiClient.post('/quizzes', data);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Failed to create quiz');
    }
  },

  // Update a quiz
  updateQuiz: async (quizId: number, data: UpdateQuizData): Promise<Quiz> => {
    try {
      const response = await apiClient.put(`/quizzes/${quizId}`, data);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Failed to update quiz');
    }
  },

  // Delete a quiz
  deleteQuiz: async (quizId: number): Promise<void> => {
    try {
      await apiClient.delete(`/quizzes/${quizId}`);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Failed to delete quiz');
    }
  },

  // Get available questions for creating a quiz
  getAvailableQuestions: async (quizId?: number, filters?: {
    search?: string;
    document_id?: number;
    type?: string;
    difficulty_min?: number;
    difficulty_max?: number;
    per_page?: number;
    page?: number;
  }): Promise<AvailableQuestionsResponse> => {
    try {
      const params = new URLSearchParams();

      if (filters?.search) params.append('search', filters.search);
      if (filters?.document_id) params.append('document_id', filters.document_id.toString());
      if (filters?.type) params.append('type', filters.type);
      if (filters?.difficulty_min) params.append('difficulty_min', filters.difficulty_min.toString());
      if (filters?.difficulty_max) params.append('difficulty_max', filters.difficulty_max.toString());
      if (filters?.per_page) params.append('per_page', filters.per_page.toString());
      if (filters?.page) params.append('page', filters.page.toString());

      const url = quizId
        ? `/quizzes/${quizId}/questions/available?${params}`
        : `/questions?${params}`;

      const response = await apiClient.get(url);

      // Handle different response formats
      if (response.data.questions) {
        return response.data;
      } else if (response.data.data) {
        return {
          questions: response.data.data.data || response.data.data,
          total: response.data.data.total || response.data.data.length,
        };
      }

      return {
        questions: response.data,
        total: response.data.length,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Failed to fetch available questions');
    }
  },

  // ============================================
  // QUIZ TAKING (Public/Guest access)
  // ============================================

  // Get quiz information for taking (public access)
  getQuizForTaking: async (quizId: number): Promise<QuizTakeInfo> => {
    try {
      const response = await apiClient.get(`/quiz/${quizId}`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Failed to fetch quiz for taking');
    }
  },

  // Test authentication
  testAuth: async (): Promise<any> => {
    try {
      const response = await apiClient.get('/test-auth');
      return response.data;
    } catch (error) {
      console.error('Auth test failed:', error);
      throw error;
    }
  },

  // Start a quiz attempt
  startQuiz: async (quizId: number, data?: StartQuizData): Promise<QuizAttempt> => {
    try {
      // Transform data to match backend expectations
      const requestData: any = {};
      if (data?.user_info?.name) {
        requestData.guest_name = data.user_info.name;
      }
      if (data?.user_info?.email) {
        requestData.guest_email = data.user_info.email;
      }

      const response = await apiClient.post(`/quiz/${quizId}/start`, requestData);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Failed to start quiz');
    }
  },

  // Submit quiz answers
  submitQuiz: async (quizId: number, data: SubmitQuizData): Promise<SubmitQuizResponse> => {
    try {
      const response = await apiClient.post(`/quiz/${quizId}/submit`, data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Failed to submit quiz');
    }
  },

  // Get quiz results
  getQuizResults: async (attemptId: number): Promise<QuizResult> => {
    try {
      const response = await apiClient.get(`/quiz/attempt/${attemptId}/results`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Failed to fetch quiz results');
    }
  },

  // ============================================
  // USER QUIZ HISTORY (Authenticated users)
  // ============================================

  // Get user's quiz attempts
  getUserAttempts: async (page: number = 1, filters?: {
    quiz_id?: number;
    per_page?: number;
  }): Promise<UserQuizAttempts> => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        ...(filters?.quiz_id && { quiz_id: filters.quiz_id.toString() }),
        ...(filters?.per_page && { per_page: filters.per_page.toString() }),
      });

      const response = await apiClient.get(`/quiz-attempts?${params}`);

      // Handle Laravel pagination response format
      const paginatedData = response.data.data;
      return {
        attempts: paginatedData.data,
        total: paginatedData.total,
        current_page: paginatedData.current_page,
        last_page: paginatedData.last_page,
        per_page: paginatedData.per_page,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Failed to fetch user attempts');
    }
  },

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  // Generate quiz share URL
  getQuizShareUrl: (quizId: number): string => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/quiz/${quizId}`;
  },

  // Format time duration (seconds to human readable)
  formatTimeDuration: (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds} giây`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return remainingSeconds > 0
        ? `${minutes} phút ${remainingSeconds} giây`
        : `${minutes} phút`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = seconds % 60;

      let result = `${hours} giờ`;
      if (minutes > 0) result += ` ${minutes} phút`;
      if (remainingSeconds > 0) result += ` ${remainingSeconds} giây`;

      return result;
    }
  },

  // Calculate quiz statistics
  calculateQuizStats: (results: QuizQuestionResult[]) => {
    const totalQuestions = results.length;
    const correctAnswers = results.filter(r => r.is_correct).length;
    const incorrectAnswers = totalQuestions - correctAnswers;
    const totalScore = results.reduce((sum, r) => sum + r.points_earned, 0);
    const maxScore = results.reduce((sum, r) => sum + (r.points_earned / (r.is_correct ? 1 : 0) || 0), 0);
    const percentage = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;

    return {
      totalQuestions,
      correctAnswers,
      incorrectAnswers,
      totalScore,
      maxScore,
      percentage,
    };
  },

  // Validate quiz settings
  validateQuizSettings: (data: CreateQuizData | UpdateQuizData) => {
    const errors: string[] = [];

    if ('title' in data && (!data.title || data.title.trim().length < 3)) {
      errors.push('Tiêu đề bài tập phải có ít nhất 3 ký tự');
    }

    if ('question_ids' in data && (!data.question_ids || data.question_ids.length === 0)) {
      errors.push('Bài tập phải có ít nhất 1 câu hỏi');
    }

    if (data.time_limit !== undefined && data.time_limit !== null && data.time_limit <= 0) {
      errors.push('Thời gian làm bài phải lớn hơn 0');
    }

    if (data.max_attempts !== undefined && data.max_attempts !== null && data.max_attempts <= 0) {
      errors.push('Số lần làm bài tối đa phải lớn hơn 0');
    }

    return errors;
  },

  // Get quiz attempts for admin/teacher
  getQuizAttempts: async (
    quizId: number,
    params?: {
      search?: string;
      min_score?: number;
      max_score?: number;
      from_date?: string;
      to_date?: string;
      per_page?: number;
      page?: number;
    }
  ): Promise<{
    success: boolean;
    data: {
      data: Array<{
        id: number;
        user_id?: number;
        user?: {
          id: number;
          name: string;
          email: string;
        };
        user_info?: {
          name?: string;
          email?: string;
        };
        score: number;
        total_score: number;
        percentage: number;
        time_taken: number; // in seconds
        started_at: string;
        submitted_at: string;
        ip_address?: string;
      }>;
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
    };
    stats: {
      total_attempts: number;
      average_score: number;
      highest_score: number;
      lowest_score: number;
      average_time: number; // in minutes
    };
    quiz: {
      id: number;
      title: string;
      description?: string;
      created_at: string;
    };
  }> => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.search) queryParams.append('search', params.search);
      if (params?.min_score !== undefined) queryParams.append('min_score', params.min_score.toString());
      if (params?.max_score !== undefined) queryParams.append('max_score', params.max_score.toString());
      if (params?.from_date) queryParams.append('from_date', params.from_date);
      if (params?.to_date) queryParams.append('to_date', params.to_date);
      if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
      if (params?.page) queryParams.append('page', params.page.toString());

      const response = await apiClient.get(`/quizzes/${quizId}/attempts?${queryParams}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Failed to fetch quiz attempts');
    }
  },
};

export default quizService; 